'use client'

import Link from 'next/link'
import { useRef, useState } from 'react'
import { LuPlus } from 'react-icons/lu'

import { Button, buttonStyles } from '@/core/components/form/button/button'
import { toast } from '@/core/lib/toast'

import PersonaCard from '@/features/veluvit/components/account/persona-card'
import { SheetHead } from '@/features/veluvit/components/overlay/layer-parts'
import PageHeader from '@/features/veluvit/components/shell/page-header'
import EmptyState from '@/features/veluvit/components/ui/empty-state'
import { useAppData } from '@/features/veluvit/hooks/use-app-data'
import { openAuth } from '@/features/veluvit/services/account.actions'
import { api } from '@/features/veluvit/services/api'
import { refreshSession } from '@/features/veluvit/services/session.service'
import { closeLayer, openModal } from '@/features/veluvit/stores/overlay.store'
import { useSessionStore } from '@/features/veluvit/stores/session.store'

import type { SavedPersona } from '@/features/veluvit/types/api.types'

export function AccountHeading({ title, sub }: { title: string; sub: string }) {
  return (
    <div className="mx-auto max-w-[760px] px-4 pt-6 pb-7 over10:px-8 over10:pt-10">
      <h1 className="sr-only font-bold tracking-tight over10:not-sr-only over10:text-c26">
        {title}
      </h1>
      <p className="mt-2 text-c14 leading-relaxed text-t3 over10:text-c15">
        {sub}
      </p>
    </div>
  )
}

function confirmDelete(
  persona: SavedPersona,
  remove: (done: () => void) => Promise<void>
) {
  const layerId: string = openModal(
    <>
      <SheetHead title="페르소나를 삭제할까요?" />
      <p className="text-c14 leading-relaxed text-t2">
        {persona.name}을 목록에서 삭제해요. 진행 중인 대화의 설정과 기록은
        유지돼요.
      </p>
      <div className="mt-6 flex justify-end gap-2">
        <Button variant="secondary" onClick={() => closeLayer(layerId)}>
          취소
        </Button>
        <Button
          variant="danger"
          onClick={() => void remove(() => closeLayer(layerId))}
        >
          삭제하기
        </Button>
      </div>
    </>,
    { label: '페르소나 삭제', className: 'max-w-[440px]!' }
  )
}

export default function PersonasScreen() {
  const { data } = useAppData()
  const user = data?.user
  const people = [...(data?.personas || [])].sort(
    (a, b) => Number(b.isDefault) - Number(a.isDefault)
  )
  const [busy, setBusy] = useState<string | null>(null)
  const lock = useRef(false)
  const mutate = async (
    persona: SavedPersona,
    action: 'default' | 'delete',
    done?: () => void
  ) => {
    if (lock.current) return
    const identity = useSessionStore.getState().identity
    lock.current = true
    setBusy(persona.id)
    try {
      await api(
        `/persona/${persona.id}/`,
        action === 'default' ? { isDefault: true } : undefined,
        action === 'default' ? 'PATCH' : 'DELETE'
      )
      if (useSessionStore.getState().identity !== identity) return
      await refreshSession()
      if (useSessionStore.getState().identity !== identity) return
      done?.()
      toast.show(
        action === 'default'
          ? '새 대화의 기본 페르소나로 설정했어요'
          : '페르소나를 삭제했어요'
      )
    } catch (cause) {
      if (useSessionStore.getState().identity === identity)
        toast.show(
          cause instanceof Error ? cause.message : '다시 시도해 주세요.'
        )
    } finally {
      lock.current = false
      setBusy(null)
    }
  }
  const canSave = !!user && !user.guest
  return (
    <>
      <PageHeader back title="내 페르소나" />
      <AccountHeading
        title="내 페르소나"
        sub="이야기마다 다른 이름과 설정으로 만나 보세요."
      />
      <section className="mx-auto max-w-[760px] px-4 pb-14 over10:px-8">
        <div className="flex items-center justify-between gap-3 border-b border-border pb-4">
          <span className="text-c13 text-t3 tabular-nums">
            저장한 페르소나 {people.length} / 12
          </span>
          {canSave ? (
            <Link
              href="/persona-edit/new"
              aria-disabled={people.length >= 12}
              tabIndex={people.length >= 12 ? -1 : undefined}
              onClick={
                people.length >= 12
                  ? (event) => event.preventDefault()
                  : undefined
              }
              className={buttonStyles({
                variant: 'primary',
                size: 'sm',
                className:
                  people.length >= 12 ? 'pointer-events-none opacity-40' : '',
              })}
            >
              <LuPlus aria-hidden="true" className="size-4" />새 페르소나
            </Link>
          ) : (
            <Button
              variant="primary"
              size="sm"
              onClick={() => openAuth('login')}
            >
              로그인
            </Button>
          )}
        </div>
        {!canSave ? (
          <EmptyState
            title="어떤 나로 만나고 싶나요?"
            description="로그인하면 사진과 설정을 저장하고 여러 대화에서 다시 사용할 수 있어요."
            action={
              <Button onClick={() => openAuth('login')}>
                로그인하고 만들기
              </Button>
            }
          />
        ) : people.length ? (
          <div>
            {people.map((persona) => (
              <PersonaCard
                key={persona.id}
                persona={persona}
                busy={!!busy}
                onDefault={() => void mutate(persona, 'default')}
                onDelete={() =>
                  confirmDelete(persona, (done) =>
                    mutate(persona, 'delete', done)
                  )
                }
              />
            ))}
          </div>
        ) : (
          <EmptyState
            title="첫 페르소나를 만들어 보세요"
            description="대화에서 불릴 이름만 있어도 시작할 수 있어요. 사진과 소개는 나중에 채워도 돼요."
            action={
              <Link
                href="/persona-edit/new"
                className={buttonStyles({ variant: 'primary' })}
              >
                페르소나 만들기
              </Link>
            }
          />
        )}
        <p className="border-t border-border pt-5 text-c13 leading-relaxed text-t3">
          기본 페르소나는 새 채팅을 시작할 때 먼저 선택돼요. 진행 중인 대화의
          설정은 채팅 메뉴에서 바꿀 수 있어요.
        </p>
      </section>
    </>
  )
}
